/**
 * A submitted request registers immediately but is validated asynchronously:
 * the ledger's verdict appears on `GET /head/requests/<id>` as
 * `status.validity` a moment later. Poll for it so the UI can report what
 * actually happened instead of celebrating mere registration.
 *
 * `unknown` = no verdict within the window (report "pending", not success).
 * The rejection REASON is not exposed by the head's API today, so a rejected
 * request can only be reported generically.
 */
export type RequestVerdict = "valid" | "invalid" | "unknown";

export async function awaitRequestVerdict(
  requestId: number,
  { timeoutMs = 15_000, intervalMs = 600 }: { timeoutMs?: number; intervalMs?: number } = {},
): Promise<RequestVerdict> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const response = await fetch(
        `${window.__APP_CONFIG.apiBaseUrl}/head/requests/${requestId}`,
      );
      if (response.ok) {
        const data = await response.json();
        const validity = data?.status?.validity;
        if (validity === "valid" || validity === "invalid") return validity;
      }
    } catch {
      /* transient -- keep polling until the deadline */
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  return "unknown";
}
